import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { filter, Subscription, map, take } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import {
	getStateUniversity,
	getStateIsLoading,
} from '../../data-access/+state/university.selectors';
import {
	fetchUniByProduct,
	fetchUniversity,
} from '../../data-access/+state/university.actions';
import {
	ClientFeatures,
	FeaturedPostgraduate,
	FeaturedUndergraduate,
	Gallery,
} from '@uc/web/shared/data-models';
import {
	CtaNonClearingComponent,
	UNI_SLIDER_COPY,
	UniversitySliderComponent,
} from '@uc/web/shared/features/feature-university-slider';
import { fetchAllEvents, getUndergraduateEventsData } from '@uc/web/shared/data-access';
import { SeoMetadataService, isStoreEmpty } from '@uc/utilities';
import { ClientValidatorService } from '@uc/web/core';
import { AsyncPipe, NgClass } from '@angular/common';
import { UniversitySkeletonComponent } from '../../components/university-skeleton/university-skeleton.component';
import { UniversityHeaderTitleComponent } from '../../components/university-header-title/university-header-title.component';
import { UniversityInformationComponent } from '../../components/university-information/university-information.component';
import { HeadingComponent } from '@uc/shared/ui';
import { CourseSearchBannerComponent, UniversityEventsComponent } from '@web/shared/ui';
import { CampusLocationsComponent } from '../../features/campus-locations/campus-locations.component';
import { UniversitySectionsComponent } from '../../components/university-sections/university-sections.component';
import { UniversityAccommodationComponent } from '../../features/university-accommodation/university-accommodation.component';
import {
	ClientProfileStickyLeadBarDesktopComponent,
	ClientProfileStickyLeadBarMobileComponent,
} from '@web/shared/features/feature-sticky-lead-bar';
import { RecommendedUnisGridComponent } from '@uc/web/shared/features/feature-recommended-universities';
import { VisitWebsiteBtnComponent } from '@uc/web/shared/buttons';
import { NA, UniversityGAService, VideoGAService } from '@uc/web/analytics';
import { Product, ProductEnum } from '@uc/shared/data-models';

@Component({
	selector: 'uc-university-profile',
	templateUrl: './undergraduate-profile.component.html',
	imports: [
		NgClass,
		AsyncPipe,
		UniversitySkeletonComponent,
		UniversityHeaderTitleComponent,
		UniversityInformationComponent,
		HeadingComponent,
		VisitWebsiteBtnComponent,
		CampusLocationsComponent,
		UniversityEventsComponent,
		CourseSearchBannerComponent,
		UniversitySectionsComponent,
		UniversityAccommodationComponent,
		ClientProfileStickyLeadBarDesktopComponent,
		ClientProfileStickyLeadBarMobileComponent,
		RecommendedUnisGridComponent,
		CtaNonClearingComponent,
		UniversitySliderComponent,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UndergraduateProfileComponent implements OnInit, OnDestroy {
	readonly paidFeatures = [ClientFeatures.Undergraduate];
	readonly paidFeaturesGallery = [...this.paidFeatures];
	uniGallery!: Gallery;
	product: Product = ProductEnum.Undergraduate;
	/**
	 * when the user navigates to a different profile (e.g. undergraduate -> postgraduate)
	 * @isLoading$ will be true and the UI will display the loading state
	 */
	isLoading$ = this._store.pipe(select(getStateIsLoading));
	universityUrl = this._route.parent?.snapshot.params['uniName'];
	isFeaturedGallery!: FeaturedUndergraduate | FeaturedPostgraduate;
	isFeaturedUndergraduate!: FeaturedUndergraduate;
	uniSliderCopy = { ...UNI_SLIDER_COPY.uni };

	university$ = this._store.pipe(
		select(getStateUniversity),
		filter((university) => !isStoreEmpty(university)),
		map((university) => {
			if (!university) return;

			this.isFeaturedUndergraduate = this._cvSrv.checkFeature(
				university?.paid_features,
				this.paidFeatures,
			);
			this.isFeaturedGallery = this._cvSrv.checkFeature(
				university?.paid_features,
				this.paidFeaturesGallery,
			);

			this.uniGallery = {
				cover_image: university.undergraduate?.cover_image,
				images: university.universityGallery.images,
				video: university.universityGallery.video,
			};

			this.handleSEO(university.seo);

			return university;
		}),
	);

	events$ = this._store.pipe(select(getUndergraduateEventsData));

	private _routeSub!: Subscription | undefined;
	private _isPreview = this._route.parent?.snapshot.queryParams['preview'] === 'true';

	constructor(
		private _store: Store,
		private _route: ActivatedRoute,
		private _seoSrv: SeoMetadataService,
		private _uniGASrv: UniversityGAService,
		private _cvSrv: ClientValidatorService,
		private _videoGASrv: VideoGAService,
	) {}

	ngOnInit(): void {
		// Display the preview view only if the preview query param is present in the url
		if (this._isPreview) {
			this.paidFeatures.push(ClientFeatures.UnderPreview);
			this.paidFeaturesGallery.push(ClientFeatures.UnderPreview);
			this.paidFeaturesGallery.push(ClientFeatures.PostPreview);
		}

		this._routeSub = this._route.parent?.paramMap.subscribe((params) => {
			this.universityUrl = params.get('uniName');
			const payload = {
				universityUrl: this.universityUrl,
				product: this.product,
			};

			this._store.dispatch(fetchUniversity(payload));
			this._store.dispatch(fetchUniByProduct(payload));
		});

		this._store.dispatch(
			fetchAllEvents({
				uniSlug: this.universityUrl,
			}),
		);

		// GA event
		this.onUniProfileImpression();
	}

	handleSEO(universitySEO: { title: string; description: string }): void {
		this._seoSrv.updateTitle(universitySEO['title']);
		this._seoSrv.updateDescription(universitySEO['description']);
	}

	ngOnDestroy(): void {
		this._routeSub?.unsubscribe();
	}

	onUniProfileImpression() {
		this.university$
			.pipe(
				take(1),
				map((uni) => {
					if (uni) {
						const { id, name, order, address } = uni;
						const degree = 'Undergraduate';

						this._uniGASrv.uniImpression(
							id,
							name,
							order,
							null,
							this.product,
							null,
							NA,
							degree,
							address?.region || NA,
							degree,
						);

						this._uniGASrv.uniProfileView(
							id,
							name,
							order,
							null,
							this.product,
						);

						if (this.uniGallery.video?.url) {
							this._videoGASrv.impression(id, this.product);
						}
					}
				}),
			)
			.subscribe();
	}
}
